import { useState, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import { Typography, message } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { invitationApi } from '../../api/businessApi';
import { useBusinessStore } from '../../store/businessStore';
import { telegram } from '../../utils/telegram';
import InvitationsHeroCard from './InvitationsHeroCard';
import InvitationRow from './InvitationRow';
import ConfirmSheet from '../provider/shared/ConfirmSheet';
import type { Invitation } from '../../types/business';

const { Text } = Typography;

export interface InvitationsSectionHandle {
  refresh: () => Promise<void>;
}

const InvitationsSection = forwardRef<InvitationsSectionHandle, object>(
  function InvitationsSection(_props, ref) {
    const { t, i18n } = useTranslation('owner');
    const { selectedBusiness } = useBusinessStore();
    const tenantId = selectedBusiness?.id;

    const [invitations, setInvitations] = useState<Invitation[]>([]);
    const [phone, setPhone] = useState('');
    const [sending, setSending] = useState(false);
    const [revokeTarget, setRevokeTarget] = useState<Invitation | null>(null);
    const [revoking, setRevoking] = useState(false);

    const loadInvitations = useCallback(async () => {
      if (!tenantId) return;
      try {
        const data = await invitationApi.getAll(tenantId);
        setInvitations(data);
      } catch {
        void message.error(t('invitations.loadError'));
      }
    }, [tenantId, t]);

    useImperativeHandle(ref, () => ({ refresh: loadInvitations }), [loadInvitations]);

    useEffect(() => {
      void loadInvitations();
    }, [loadInvitations]);

    // Reload on language change
    useEffect(() => {
      const handler = () => { void loadInvitations(); };
      i18n.on('languageChanged', handler);
      return () => { i18n.off('languageChanged', handler); };
    }, [i18n, loadInvitations]);

    if (!tenantId) return null;

    const handleInvite = async () => {
      const cleaned = phone.replace(/[\s\-()]/g, '');
      if (cleaned.length < 9) {
        void message.warning(t('invitations.invalidPhone'));
        return;
      }
      setSending(true);
      try {
        const created = await invitationApi.invite(tenantId, cleaned);
        setInvitations((prev) => [created, ...prev]);
        setPhone('');
        telegram.hapticFeedback.notification('success');
        void message.success(t('invitations.sentMsg'));
      } catch (err: unknown) {
        const error = err as {
          response?: { status?: number; data?: { message?: string; code?: string } };
        };
        const code = error?.response?.data?.code;
        if (code === 'PROVIDER_ALREADY_INVITED') {
          void message.error(t('invitations.alreadyInvited'));
        } else if (error?.response?.status === 403) {
          void message.error(t('invitations.limitReached'));
        } else {
          void message.error(error?.response?.data?.message ?? t('invitations.sendError'));
        }
        telegram.hapticFeedback.notification('error');
      } finally {
        setSending(false);
      }
    };

    const handleRevokeConfirm = async () => {
      if (!revokeTarget) return;
      const target = revokeTarget;
      setRevoking(true);
      try {
        await invitationApi.revoke(tenantId, target.id);
        setInvitations((prev) => prev.filter((inv) => inv.id !== target.id));
        telegram.hapticFeedback.notification('success');
        void message.success(t('invitations.revokedMsg'));
        setRevokeTarget(null);
      } catch {
        void message.error(t('invitations.revokeError'));
        telegram.hapticFeedback.notification('error');
      } finally {
        setRevoking(false);
      }
    };

    const pendingCount = invitations.filter((inv) => inv.status === 'PENDING').length;
    const hasInvitations = invitations.length > 0;

    return (
      <>
        {/* Invitation hero (form + caption + pending badge) */}
        <div className="bl-animate-in">
          <InvitationsHeroCard
            pendingCount={pendingCount}
            phone={phone}
            onPhoneChange={setPhone}
            onSend={() => void handleInvite()}
            sending={sending}
          />
        </div>

        {/* Invitations list */}
        {hasInvitations && (
          <>
            <div
              className="bl-animate-in bl-animate-in-delay-1"
              style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, paddingLeft: 4 }}
            >
              <MailOutlined style={{ fontSize: 11, color: 'var(--bl-text-muted, #9ca3af)' }} />
              <Text
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'var(--bl-text-muted, #9ca3af)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                {t('invitations.title')}
              </Text>
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              {invitations.map((invitation, idx) => {
                const delayClass =
                  idx < 2 ? 'bl-animate-in bl-animate-in-delay-1' :
                  idx < 4 ? 'bl-animate-in bl-animate-in-delay-2' :
                  'bl-animate-in bl-animate-in-delay-3';
                return (
                  <div key={invitation.id} className={delayClass}>
                    <InvitationRow
                      invitation={invitation}
                      onRevoke={(inv) => setRevokeTarget(inv)}
                    />
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Revoke confirm sheet */}
        <ConfirmSheet
          open={revokeTarget !== null}
          title={t('invitations.revokeConfirm')}
          confirmText={t('invitations.yes')}
          cancelText={t('invitations.no')}
          danger
          loading={revoking}
          onConfirm={() => void handleRevokeConfirm()}
          onCancel={() => !revoking && setRevokeTarget(null)}
        />
      </>
    );
  },
);

export default InvitationsSection;
